import type { CaptureResult } from 'posthog-js';

const MAX_DEPTH = 12;
const MASK = ':token';

const PAY_LINK_TOKEN =
  /(\/pay(?:-invoice)?\/)(?!:token(?=[/?#"'\s]|$))[^/?#"'\s]+/g;
const ENCODED_PAY_LINK_TOKEN =
  /(%2Fpay(?:-invoice)?%2F)(?!%3Atoken)(?:(?!%2F|%3F|%23)[^/?#&"'\s])+/gi;

const URL_KEYS = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
];

const HREF_LIKE_KEY = /href|url|path|referrer|location/i;

const maskString = (value: string) =>
  value
    .replace(PAY_LINK_TOKEN, `$1${MASK}`)
    .replace(ENCODED_PAY_LINK_TOKEN, `$1%3Atoken`);

export const maskPayLinkString = maskString;

const isPlainObject = (value: object) => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const maskValue = (
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
  hrefOnly: boolean
): unknown => {
  if (typeof value === 'string') return hrefOnly ? value : maskString(value);
  if (value === null || typeof value !== 'object' || depth > MAX_DEPTH) {
    return value;
  }
  if (seen.has(value)) return value;

  if (Array.isArray(value)) {
    seen.add(value);
    return value.map(item => maskValue(item, depth + 1, seen, hrefOnly));
  }
  if (!isPlainObject(value)) return value;
  seen.add(value);

  const next: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    const childHrefOnly = hrefOnly || key === '$snapshot_data';
    const masked =
      childHrefOnly && typeof item === 'string'
        ? HREF_LIKE_KEY.test(key)
          ? maskString(item)
          : item
        : maskValue(item, depth + 1, seen, childHrefOnly);
    next[hrefOnly ? key : maskString(key)] = masked;
  }
  return next;
};

const maskRecord = <T extends Record<string, unknown> | undefined>(
  record: T
): T =>
  record ? (maskValue(record, 0, new WeakSet<object>(), false) as T) : record;

const maskKnownUrls = <T extends Record<string, unknown> | undefined>(
  record: T
): T => {
  if (!record) return record;
  const next: Record<string, unknown> = { ...record };
  for (const key of URL_KEYS) {
    const value = next[key];
    if (typeof value === 'string') next[key] = maskString(value);
  }
  return next as T;
};

export const maskPayLinkTokens = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) return event;
  try {
    return {
      ...event,
      properties: maskRecord(event.properties),
      $set: maskRecord(event.$set),
      $set_once: maskRecord(event.$set_once),
    };
  } catch {
    try {
      return {
        ...event,
        properties: maskKnownUrls(event.properties),
        $set: maskKnownUrls(event.$set),
        $set_once: maskKnownUrls(event.$set_once),
      };
    } catch {
      return event;
    }
  }
};
